import type { JobBoard, JobBoardResponse } from '@/core/collections/job-boards/job-board.interface';
import { ContentBlock } from '@/modules/content-blocks/interfaces';
import { Language } from '@future-harry/nxt-vue-library';
import { JobBoardMetaDataKey, SocialUrlsDataKey } from './job-board.interface';
import { JOB_BOARD_ENDPOINTS } from './job-board.endpoints';
import { getUrlDomainName } from '@/core/utils';

export const createDefaultJobBoard = (): JobBoard => ({
   apply_spontaneous_url: '',
   border_radius: '',
   contact_email: '',
   created_at: '',
   favicon_url: '',
   font_family: '',
   home_url: '',
   id: 0,
   is_published: false,
   logo_image_url: '',
   meta_data: {
      [JobBoardMetaDataKey.Title]: '',
      [JobBoardMetaDataKey.Description]: '',
      [JobBoardMetaDataKey.About]: '',
      [JobBoardMetaDataKey.Keywords]: '',
      [JobBoardMetaDataKey.OpenGraph]: '',
   },
   name: '',
   primary_color: '',
   secondary_color: '',
   social_urls: {
      [SocialUrlsDataKey.Facebook]: '',
      [SocialUrlsDataKey.X]: '',
      [SocialUrlsDataKey.LinkedIn]: '',
      [SocialUrlsDataKey.Instagram]: '',
   },
   tenant_id: 0,
   updated_at: '',
   url: '',
   tenant: undefined,
   primary_font_color: '',
   secondary_font_color: '',
   languages: [],
   language_ids: [],
   default_language_id: 0,
   address: {},
   cookie_policy: {},
   privacy_policy: {},
   terms_of_service: {},
   categories: [],
   customers: [],
   application_fields: {
      cv: null,
      name: null,
      email: null,
      phone: null,
      photo: null,
      address: null,
      motivation: null,
      date_of_birth: null,
   },
});

/*
 * builds the request configuration for fetching a job board
 * - if id is provided, fetch the job board by id
 * - if id is not provided
 *       use GET_ALL (/job-boards)  !this does not fetch all job-boards, but the job board matching the current domain
 *       in case of local development provide a url param to specify which job board to fetch to mimic the domain
 *       ex. params.url = 'https://volo.nxtpeople.jobs';
 */
export const buildJobBoardRequestConfig = (id?: string) => {
   let url = null;
   const params: Record<string, any> = {};

   const includeFields = [
      'languages',
      'pages.latest_version',
      'categories',
      'category_images',
      'customers',
      'address',
      'address.city',
      'address.country',
   ];

   params.include = includeFields.join(',');

   if (id === undefined) {
      url = JOB_BOARD_ENDPOINTS.GET_ALL();
      if (import.meta.env.DEV) {
         // for local development, specify which job board to fetch since the domain is localhost
         // params.url = 'https://westvlees.nxtpeople.jobs';
      }
   } else {
      url = JOB_BOARD_ENDPOINTS.GET_BY_ID(id);
   }

   return { url, params };
};

const getCSSVariableValue = (variableName: string): string => {
   return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};

export const mapJobBoard = (data: JobBoardResponse): JobBoard => {
   if (!data) return createDefaultJobBoard();

   const languages = data.languages ?? [];

   return {
      ...data,
      url: getUrlDomainName(data.url) ?? '',
      social_urls: {
         [SocialUrlsDataKey.Facebook]: data.social_urls?.[SocialUrlsDataKey.Facebook] ?? '',
         [SocialUrlsDataKey.X]: data.social_urls?.[SocialUrlsDataKey.X] ?? '',
         [SocialUrlsDataKey.LinkedIn]: data.social_urls?.[SocialUrlsDataKey.LinkedIn] ?? '',
         [SocialUrlsDataKey.Instagram]: data.social_urls?.[SocialUrlsDataKey.Instagram] ?? '',
      },
      meta_data: {
         [JobBoardMetaDataKey.Title]: data.meta_data?.[JobBoardMetaDataKey.Title] ?? '',
         [JobBoardMetaDataKey.Description]: data.meta_data?.[JobBoardMetaDataKey.Description] ?? '',
         [JobBoardMetaDataKey.About]: data.meta_data?.[JobBoardMetaDataKey.About] ?? '',
         [JobBoardMetaDataKey.Keywords]: data.meta_data?.[JobBoardMetaDataKey.Keywords] ?? '',
         [JobBoardMetaDataKey.OpenGraph]: data.meta_data?.[JobBoardMetaDataKey.OpenGraph] ?? '',
      },
      languages: languages.map((lang: Language) => ({
         ...lang,
         iso_639_1: lang.iso_639_1.toLowerCase(),
         iso_639_2: lang.iso_639_2.toLowerCase(),
         iso_639_3: lang.iso_639_3.toLowerCase(),
      })),
      language_ids: languages.map((lang: Language) => lang.id),
      default_language_id: data.default_language_id ?? 0,
      primary_color: data.primary_color ?? getCSSVariableValue('--nxt-light-grey'),
      secondary_color: data.secondary_color ?? '',
      border_radius: data.border_radius ?? '0',
      font_family: data.font_family ?? 'Arial',
      primary_font_color: data.primary_font_color ?? getCSSVariableValue('--nxt-dark'),
      secondary_font_color: data.secondary_font_color ?? getCSSVariableValue('--nxt-blue'),
      favicon_url: data.favicon_url ?? '',
      logo_image_url: data.logo_image_url ?? '',
      cookie_policy: data.cookie_policy ?? {},
      privacy_policy: data.privacy_policy ?? {},
      terms_of_service: data.terms_of_service ?? {},
      categories: data.categories ?? [],
      customers: data.customers ?? [],
      address: data.address
         ? {
              ...data.address,
              phone_number: data.address.phone_number?.international_format ?? null,
           }
         : {},
   };
};

export const serializeJobBoard = (data: JobBoard): any => {
   const { box, ...address } = data.address ?? {};

   return {
      ...data,
      url: `https://${data.url.trim()}.nxtpeople.jobs`,
      customer_ids: data.customers?.map((company) => company.id) ?? [],
      address: {
         ...address,
         city_id: address?.city?.id ?? null,
         box_number: box ?? null,
      },
   };
};

export const mapContentBlocks = (jobBoard: JobBoard): ContentBlock[] => {
   if (!jobBoard) return [];

   const firstPageLatestContentBlocks = jobBoard.pages?.[0]?.latest_version?.parsed_blocks ?? [];

   return firstPageLatestContentBlocks.map((block: ContentBlock, i: number) => {
      const mapped: ContentBlock = {
         ...block,
         id: `${i}_${block.type}`, // add unique id for vue draggable
      };

      return mapped;
   });
};
